import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { MOBILE_DASHBOARD_ROLES, hasRole } from './constants/roles';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { StoreContext, STORE_KEYS, getStoreSlugFromPath } from './StoreContext';

const AuthContext = createContext(null);

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser]       = useState(null);
    const [loading, setLoading] = useState(true);
    // Emulation state: { id, name, shopify_location_id } or null
    const [emulating, setEmulating] = useState(() => {
        try { return JSON.parse(localStorage.getItem('emulating_location') || 'null'); } catch { return null; }
    });

    useEffect(() => { checkAuth(); }, []);

    // When another tab enters portal mode it removes operations_token from
    // localStorage but writes the same JWT to ops_portal_token (the persistent
    // kiosk token). Other admin tabs should NOT log out just because a sibling
    // tab entered kiosk mode — restore the token from the kiosk slot so admin
    // work in this tab keeps going. Only clear user state when the kiosk
    // token is also missing (i.e. a genuine logout in another tab).
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key !== 'operations_token' || e.newValue) return;
            const kiosk = localStorage.getItem('ops_portal_token');
            if (kiosk) {
                // Sibling tab stashed for portal — restore so this admin tab
                // doesn't get bounced to /login on the next API call.
                localStorage.setItem('operations_token', kiosk);
                return;
            }
            setUser(null);
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const checkAuth = async () => {
        // In portal mode the admin token is stashed in sessionStorage.
        // Persistent kiosk mode stores it in localStorage under KIOSK_TOKEN_KEY.
        // Fall back in that order so ProtectedRoute still passes on hard navigation
        // and post-reboot boot.
        const kioskToken = localStorage.getItem('ops_portal_token');
        const token = localStorage.getItem('operations_token')
                   || sessionStorage.getItem('pressing_admin_token')
                   || kioskToken;
        if (token) {
            try {
                const res = await fetch('/api/auth/verify', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Apply any sliding-refresh header that came back
                const refreshed = res.headers.get('X-Auth-Refreshed-Token');
                if (refreshed) {
                    if (localStorage.getItem('operations_token')) localStorage.setItem('operations_token', refreshed);
                    if (sessionStorage.getItem('pressing_admin_token')) sessionStorage.setItem('pressing_admin_token', refreshed);
                    if (localStorage.getItem('ops_portal_token')) localStorage.setItem('ops_portal_token', refreshed);
                }
                if (res.ok) {
                    const data = await res.json();
                    // The test site shares the live database, so only people with the
                    // "Test site access" Extra Permission may stay; everyone else goes to prod.
                    if (window.location.hostname.includes('operations-test')
                        && !(data.permissions || []).includes('system.test_site')) {
                        window.location.replace('https://operations.soccerpost.com'
                            + window.location.pathname + window.location.search + window.location.hash);
                        return;
                    }
                    setUser(data);
                    // Register the ops_auth HTTP-only cookie so direct GETs
                    // (e.g. the reprint-label link pasted in Slack) auth
                    // with the recipient's own session. Fire-and-forget.
                    fetch('/api/auth/set-cookie', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` },
                    }).catch(() => {});
                    // Kiosk boot: if the tab was opened fresh (no sessionStorage,
                    // no operations_token, but kiosk token present) and we're not
                    // already on the PIN screen or active portal, auto-navigate
                    // to the PIN login.
                    if (kioskToken
                        && !localStorage.getItem('operations_token')
                        && !sessionStorage.getItem('pressing_admin_token')
                        && !window.location.pathname.startsWith('/pressing-portal')
                        && !window.location.pathname.startsWith('/picking-portal')
                        && !window.location.pathname.startsWith('/packing-portal')) {
                        // Promote the kiosk token into the active portal session
                        sessionStorage.setItem('pressing_admin_token', kioskToken);
                        window.location.replace('/pressing-portal/login');
                        return;
                    }
                } else {
                    localStorage.removeItem('operations_token');
                    // If the kiosk token itself is invalid/expired, drop it.
                    if (kioskToken && res.status === 401) {
                        localStorage.removeItem('ops_portal_token');
                    }
                }
            } catch {
                localStorage.removeItem('operations_token');
            }
        }
        setLoading(false);
    };

    const login = async (username, password) => {
        const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });
        if (res.ok) {
            const data = await res.json();
            localStorage.setItem('operations_token', data.access_token);
            // Fetch the full user profile (including locations) BEFORE setting
            // the user state. Setting a partial JWT-only user first would cause
            // the post-login navigate to fire with user.locations = [] and
            // fall back to /store/soccerpost/orders for non-admin users, who
            // then hit a 403 "No access to Soccer Post" guard.
            await checkAuth();
            return { success: true };
        }
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.detail || 'Login failed' };
    };

    const logout = () => {
        localStorage.removeItem('operations_token');
        localStorage.removeItem('emulating_location');
        fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        setUser(null);
        setEmulating(null);
    };

    const startEmulation = (location) => {
        setEmulating(location);
        localStorage.setItem('emulating_location', JSON.stringify(location));
    };

    const stopEmulation = () => {
        setEmulating(null);
        localStorage.removeItem('emulating_location');
    };

    // 2026-09-15 (Eric): per-user preference toggles (e.g. the v2 order view).
    // Updates the in-memory user at once so the UI reacts immediately, then
    // shallow-merges the patch into users.preferences on the server.
    const patchPreferences = useCallback(async (patch) => {
        setUser(u => u ? { ...u, preferences: { ...(u.preferences || {}), ...patch } } : u);
        try {
            await authenticatedFetch('/api/users/me/preferences', {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
            });
        } catch { /* keep the optimistic value; verify() reloads the truth next time */ }
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, emulating, startEmulation, stopEmulation, patchPreferences }}>
            {children}
        </AuthContext.Provider>
    );
};

// ── Kiosk token helpers ───────────────────────────────────────────────────────
// ops_portal_token is a persistent copy of the admin JWT that survives tab close
// and browser restart. It's written whenever the admin enters the portal and
// keeps rolling forward on every API call (via the X-Auth-Refreshed-Token header).

export const KIOSK_TOKEN_KEY = 'ops_portal_token';

export const getKioskToken = () => localStorage.getItem(KIOSK_TOKEN_KEY);
export const setKioskToken = (token) => {
    if (token) localStorage.setItem(KIOSK_TOKEN_KEY, token);
};
export const clearKioskToken = () => localStorage.removeItem(KIOSK_TOKEN_KEY);

// Applies the rolling-refresh token returned by the server to every storage
// key that currently holds the old token. Called from every fetch wrapper.
const applyRefreshedToken = (res) => {
    const newToken = res.headers.get('X-Auth-Refreshed-Token');
    if (!newToken) return;
    if (localStorage.getItem('operations_token')) {
        localStorage.setItem('operations_token', newToken);
    }
    if (sessionStorage.getItem('pressing_admin_token')) {
        sessionStorage.setItem('pressing_admin_token', newToken);
    }
    if (localStorage.getItem(KIOSK_TOKEN_KEY)) {
        localStorage.setItem(KIOSK_TOKEN_KEY, newToken);
    }
};

// Operator (PIN user) on every Picking / Pressing / Packing portal request (2026-10-02, Eric):
// the kiosk's login account authorizes the call, but the person doing the work is the PIN
// user. Sending them as headers lets the backend put the operator on every log line for
// the request ([user=kiosk>Operator Name#id …]) without touching any URL or body.
// encodeURIComponent keeps the header ASCII for names like "José Núñez".
export const portalOperatorHeaders = () => {
    try {
        const s = JSON.parse(sessionStorage.getItem('presser_session') || 'null');
        if (!s) return {};
        const h = {};
        // Same string the portals write to events as portal_username: "First Last".
        const name = `${s.first_name || ''} ${s.last_name || ''}`.trim();
        if (name) h['X-Portal-Operator'] = encodeURIComponent(name).slice(0, 300);
        if (s.presser_id) h['X-Portal-Operator-Id'] = String(s.presser_id);
        return h;
    } catch { return {}; }
};

// Already-open tabs on the test site: once the API refuses a user without the
// "Test site access" permission, send them to the live site.
if (typeof window !== 'undefined' && window.location.hostname.includes('operations-test') && !window.__testSiteGuard) {
    window.__testSiteGuard = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
        const res = await nativeFetch(...args);
        if (res.status === 403) {
            res.clone().json().then(j => {
                if (j && j.test_site_restricted) {
                    window.location.replace('https://operations.soccerpost.com'
                        + window.location.pathname + window.location.search + window.location.hash);
                }
            }).catch(() => {});
        }
        return res;
    };
}

export const authenticatedFetch = async (url, options = {}) => {
    const token = localStorage.getItem('operations_token');
    const emulating = (() => {
        try { return JSON.parse(localStorage.getItem('emulating_location') || 'null'); } catch { return null; }
    })();
    const headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        ...(emulating ? { 'X-Emulate-Location': String(emulating.id), 'X-Emulate-Shopify-Location': String(emulating.shopify_location_id || '') } : {}),
        // A PIN session only exists in a portal kiosk tab; calls a portal makes through this
        // helper (not pressingFetch) carry the operator too.
        ...portalOperatorHeaders(),
    };
    const res = await fetch(url, { ...options, headers });
    applyRefreshedToken(res);
    if (res.status === 401) {
        localStorage.removeItem('operations_token');
        window.location.href = '/login';
        throw new Error('Authentication required');
    }
    return res;
};

// Hook version of authenticatedFetch that auto-appends ?store=<activeStore>.
// Pass useStore=false for store-agnostic endpoints (auth, users, settings).
// The active store is derived first from the current URL (/store/:slug/...),
// then from the StoreContext state, then finally from localStorage. If none
// of those produce a value, the ?store= param is omitted entirely.
export const useAuthFetch = () => {
    const storeCtx = useContext(StoreContext);
    const location = useLocation();
    const urlSlug = getStoreSlugFromPath(location.pathname);
    const activeStore = urlSlug
        || storeCtx?.activeStore
        || localStorage.getItem('ops_active_store')
        || null;

    const storeFetch = useCallback((url, options = {}, useStore = true) => {
        const token = localStorage.getItem('operations_token');
        const emulating = (() => {
            try { return JSON.parse(localStorage.getItem('emulating_location') || 'null'); } catch { return null; }
        })();
        const finalUrl = useStore && activeStore && !url.includes('?store=')
            ? `${url}${url.includes('?') ? '&' : '?'}store=${activeStore}`
            : url;
        const headers = {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`,
            ...portalOperatorHeaders(),
            ...(emulating ? { 'X-Emulate-Location': String(emulating.id), 'X-Emulate-Shopify-Location': String(emulating.shopify_location_id || '') } : {}),
        };
        return fetch(finalUrl, { ...options, headers }).then(res => {
            applyRefreshedToken(res);
            if (res.status === 401) {
                localStorage.removeItem('operations_token');
                window.location.href = '/login';
                throw new Error('Authentication required');
            }
            return res;
        });
    }, [activeStore]);

    return storeFetch;
};

export const LoginForm = () => {
    // mode: 'login' | 'forgot-email' | 'forgot-pin' | 'forgot-password' | 'forgot-done'
    const [mode, setMode]         = useState('login');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError]       = useState('');
    const [info, setInfo]         = useState('');
    const [busy, setBusy]         = useState(false);

    // Forgot-password state (persists across steps within the flow)
    const [fpEmail, setFpEmail]       = useState('');
    const [fpPin, setFpPin]           = useState('');
    const [fpNewPassword, setFpNewPassword] = useState('');
    const [fpConfirm, setFpConfirm]   = useState('');

    const { user, login }         = useAuth();
    const navigate                = useNavigate();
    const location                = useLocation();

    useEffect(() => {
        if (!user) return;
        const fromPath = location.state?.from?.pathname;
        if (fromPath && fromPath !== '/' && fromPath !== '/login') {
            navigate(fromPath + (location.state?.from?.search || ''), { replace: true });
            return;
        }
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        if (isMobile && hasRole(user, MOBILE_DASHBOARD_ROLES)) {
            navigate('/', { replace: true });
            return;
        }
        const isAllStores = hasRole(user, ['admin']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance']);
        const userStores = [...new Set((user.locations || []).map(l => l.store_key).filter(Boolean))];
        const saved = localStorage.getItem('ops_active_store');
        const pool = isAllStores ? STORE_KEYS : userStores;
        const defaultStore = (saved && pool.includes(saved)) ? saved : (pool[0] || 'soccerpost');
        navigate(`/store/${defaultStore}/orders`, { replace: true });
    }, [user, navigate, location]);

    const switchTo = (next) => {
        setError('');
        setInfo('');
        setMode(next);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        const result = await login(username, password);
        if (!result.success) { setError(result.error); setBusy(false); }
    };

    const handleSendPin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: fpEmail.trim() }),
            });
            if (!res.ok) throw new Error('Could not send PIN. Try again.');
            // Always-success message — server doesn't reveal whether email exists.
            setInfo(`If an account exists for ${fpEmail.trim()}, a 6-digit PIN has been emailed. It expires in 15 minutes.`);
            setMode('forgot-pin');
        } catch (err) {
            setError(String(err.message || err));
        } finally {
            setBusy(false);
        }
    };

    const handleVerifyPin = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await fetch('/api/auth/verify-reset-pin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: fpEmail.trim(), pin: fpPin.trim() }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || 'Invalid or expired PIN.');
            }
            setMode('forgot-password');
            setInfo('PIN verified. Choose a new password.');
        } catch (err) {
            setError(String(err.message || err));
        } finally {
            setBusy(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (fpNewPassword !== fpConfirm) {
            setError('Passwords do not match.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: fpEmail.trim(),
                    pin: fpPin.trim(),
                    new_password: fpNewPassword,
                }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || 'Could not update password.');
            }
            setMode('forgot-done');
            setInfo('Password updated. You can now sign in with your new password.');
            // Reset password state but keep email pre-filled for convenience
            setFpPin('');
            setFpNewPassword('');
            setFpConfirm('');
        } catch (err) {
            setError(String(err.message || err));
        } finally {
            setBusy(false);
        }
    };

    const renderLogin = () => (
        <form onSubmit={handleSubmit}>
            <div className="pressing-login__field">
                <label className="form-label">Username</label>
                <input className="input" type="text" required autoFocus autoComplete="username" value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter your username" disabled={busy} />
            </div>
            <div className="pressing-login__field">
                <label className="form-label">Password</label>
                <input className="input" type="password" required autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" disabled={busy} />
            </div>
            {error && <div className="pressing-login__error">{error}</div>}
            <button type="submit" disabled={busy} className="btn btn--primary btn--full">
                {busy ? 'Signing in...' : 'Sign in'}
            </button>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button
                    type="button"
                    onClick={() => switchTo('forgot-email')}
                    style={{ background: 'none', border: 'none', color: '#0a6fc2', fontSize: 14, cursor: 'pointer', textDecoration: 'underline' }}
                >
                    Forgot your password?
                </button>
            </div>
        </form>
    );

    const renderForgotEmail = () => (
        <form onSubmit={handleSendPin}>
            <p className="pressing-login__subtitle" style={{ marginBottom: 16 }}>
                Enter the email on your account. We'll send you a 6-digit PIN to reset your password.
            </p>
            <div className="pressing-login__field">
                <label className="form-label">Email</label>
                <input className="input" type="email" required autoFocus autoComplete="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)} placeholder="you@example.com" disabled={busy} />
            </div>
            {error && <div className="pressing-login__error">{error}</div>}
            <button type="submit" disabled={busy || !fpEmail.trim()} className="btn btn--primary btn--full">
                {busy ? 'Sending...' : 'Send PIN'}
            </button>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button type="button" onClick={() => switchTo('login')} style={{ background: 'none', border: 'none', color: '#0a6fc2', fontSize: 14, cursor: 'pointer', textDecoration: 'underline' }}>
                    ← Back to sign in
                </button>
            </div>
        </form>
    );

    const renderForgotPin = () => (
        <form onSubmit={handleVerifyPin}>
            {info && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f7ff', border: '1px solid #b0d4f5', borderRadius: 8, fontSize: 13, color: '#1f4a7f' }}>
                    {info}
                </div>
            )}
            <div className="pressing-login__field">
                <label className="form-label">6-digit PIN</label>
                <input
                    className="input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    autoFocus
                    value={fpPin}
                    onChange={e => setFpPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    disabled={busy}
                    style={{ textAlign: 'center', letterSpacing: 8, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 24 }}
                />
            </div>
            {error && <div className="pressing-login__error">{error}</div>}
            <button type="submit" disabled={busy || fpPin.length !== 6} className="btn btn--primary btn--full">
                {busy ? 'Verifying...' : 'Verify PIN'}
            </button>
            <div style={{ marginTop: 16, textAlign: 'center', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <button type="button" onClick={() => switchTo('forgot-email')} style={{ background: 'none', border: 'none', color: '#0a6fc2', cursor: 'pointer', textDecoration: 'underline' }}>
                    Resend PIN
                </button>
                <button type="button" onClick={() => switchTo('login')} style={{ background: 'none', border: 'none', color: '#0a6fc2', cursor: 'pointer', textDecoration: 'underline' }}>
                    Cancel
                </button>
            </div>
        </form>
    );

    const renderForgotNewPassword = () => (
        <form onSubmit={handleResetPassword}>
            {info && (
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f7ff', border: '1px solid #b0d4f5', borderRadius: 8, fontSize: 13, color: '#1f4a7f' }}>
                    {info}
                </div>
            )}
            <div className="pressing-login__field">
                <label className="form-label">New password</label>
                <input className="input" type="password" required autoFocus autoComplete="new-password" value={fpNewPassword} onChange={e => setFpNewPassword(e.target.value)} disabled={busy} />
            </div>
            <div className="pressing-login__field">
                <label className="form-label">Confirm new password</label>
                <input className="input" type="password" required autoComplete="new-password" value={fpConfirm} onChange={e => setFpConfirm(e.target.value)} disabled={busy} />
            </div>
            {error && <div className="pressing-login__error">{error}</div>}
            <button type="submit" disabled={busy || !fpNewPassword || fpNewPassword !== fpConfirm} className="btn btn--primary btn--full">
                {busy ? 'Updating...' : 'Update password'}
            </button>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button type="button" onClick={() => switchTo('login')} style={{ background: 'none', border: 'none', color: '#0a6fc2', fontSize: 14, cursor: 'pointer', textDecoration: 'underline' }}>
                    Cancel
                </button>
            </div>
        </form>
    );

    const renderForgotDone = () => (
        <div>
            <div style={{ marginBottom: 20, padding: 16, background: '#ecfdf5', border: '1px solid #86d8a3', borderRadius: 8, fontSize: 14, color: '#1d6537' }}>
                ✓ {info}
            </div>
            <button type="button" onClick={() => switchTo('login')} className="btn btn--primary btn--full">
                Back to sign in
            </button>
        </div>
    );

    let subtitle = 'Sign in to your operations account';
    if (mode === 'forgot-email')    subtitle = 'Reset your password';
    if (mode === 'forgot-pin')      subtitle = 'Enter the PIN we emailed you';
    if (mode === 'forgot-password') subtitle = 'Choose a new password';
    if (mode === 'forgot-done')     subtitle = 'All set';

    return (
        <div className="pressing-login">
            <div className="pressing-login__card">
                <img src="/sp-ball-icon.png" alt="Soccer Post" className="pressing-login__icon" />
                <h1 className="pressing-login__title">Soccer Post Operations Hub</h1>
                <p className="pressing-login__subtitle">{subtitle}</p>
                {mode === 'login'           && renderLogin()}
                {mode === 'forgot-email'    && renderForgotEmail()}
                {mode === 'forgot-pin'      && renderForgotPin()}
                {mode === 'forgot-password' && renderForgotNewPassword()}
                {mode === 'forgot-done'     && renderForgotDone()}
            </div>
        </div>
    );
};

export const ProtectedRoute = ({ children, adminOnly = false, settingsAccess = false }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return (
        <div className="page-loading">
            Loading...
        </div>
    );
    if (!user) {
        // In portal mode the admin token is stashed in sessionStorage.
        // Redirect to the portal PIN screen rather than the admin login.
        if (sessionStorage.getItem('pressing_admin_token')) {
            return <Navigate to="/pressing-portal/login" replace />;
        }
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // First-login password reset gate — user must pick a new password before
    // any protected page will render. Intercepts every protected route;
    // PasswordResetGate is a standalone screen with its own logic.
    if (user.force_password_reset) {
        return <PasswordResetGate />;
    }
    if (adminOnly && !hasRole(user, ['admin'])) return <Navigate to="/" replace />;
    if (settingsAccess && !hasRole(user, ['admin', 'franchise', 'site_manager', 'store_manager', 'user', 'crm', 'customer_service', 'finance', 'external'])) return <Navigate to="/" replace />;
    return children;
};

// First-login / admin-reset gate. Full-screen "Set your new password" form.
// Until the user submits a valid new password, nothing else on the site renders.
function PasswordResetGate() {
    const { checkAuth, logout } = useAuth();
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    const validate = (pwd) => {
        if (!pwd || pwd.length < 8) return 'Password must be at least 8 characters.';
        if (!/[A-Z]/.test(pwd)) return 'Password must include an uppercase letter.';
        if (!/[\d\W_]/.test(pwd)) return 'Password must include a number or symbol.';
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        const v = validate(newPassword);
        if (v) { setError(v); return; }
        if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
        if (!currentPassword) { setError('Enter the temporary password you were emailed.'); return; }
        if (newPassword === currentPassword) { setError('New password must differ from the temporary one.'); return; }
        setSubmitting(true);
        try {
            const token = localStorage.getItem('operations_token');
            const res = await fetch('/api/auth/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Password change failed.');
                setSubmitting(false);
                return;
            }
            const data = await res.json();
            if (data?.access_token) {
                localStorage.setItem('operations_token', data.access_token);
            }
            // Full reload rather than refresh-via-React. Guarantees every
            // component remounts with the new JWT (and the new password_version)
            // — avoids a tricky state-update race where setUser + setLoading
            // + ProtectedRoute + nested StoreRoute re-entries left the gate
            // mounted alongside the routed page.
            window.location.href = '/';
        } catch (err) {
            setError('Network error. Try again.');
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#F3F4F6', padding: '24px',
        }}>
            <div style={{
                background: '#FFFFFF', maxWidth: 480, width: '100%',
                border: '1px solid #E5E7EB', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                overflow: 'hidden',
            }}>
                <div style={{ padding: '32px 36px 24px', textAlign: 'center', background: '#FFFFFF' }}>
                    <img src="/soccer-post-logo.png" alt="Soccer Post" style={{ maxWidth: 220, height: 'auto' }} />
                </div>
                <div style={{ height: 4, background: '#00798E' }} />
                <form onSubmit={onSubmit} style={{ padding: '28px 32px 32px' }}>
                    <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Set your password</h1>
                    <p style={{ margin: '0 0 20px', fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                        Welcome. To finish setting up your account, enter the temporary password from your email and then pick a new one.
                    </p>
                    {error && (
                        <div style={{
                            background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
                            padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14,
                        }}>{error}</div>
                    )}
                    <label style={{ display: 'block', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Temporary password</div>
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                               autoComplete="current-password" autoFocus
                               style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>New password</div>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                               autoComplete="new-password"
                               style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }} />
                    </label>
                    <label style={{ display: 'block', marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Confirm new password</div>
                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                               autoComplete="new-password"
                               style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14 }} />
                    </label>
                    <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 18, lineHeight: 1.4 }}>
                        Must be at least 8 characters with one uppercase letter and one number or symbol.
                    </div>
                    <button type="submit" disabled={submitting} style={{
                        width: '100%', padding: '12px 16px', background: '#00798E', color: '#FFFFFF',
                        border: 0, borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
                        opacity: submitting ? 0.7 : 1,
                    }}>{submitting ? 'Saving…' : 'Set password and continue'}</button>
                    <button type="button" onClick={logout} style={{
                        marginTop: 12, width: '100%', padding: '10px 16px', background: 'transparent',
                        color: '#6B7280', border: 0, fontSize: 13, cursor: 'pointer',
                    }}>Sign out</button>
                </form>
            </div>
        </div>
    );
}

// ── Pressing Portal session helpers ───────────────────────────────────────────

export const stashPressingAdminToken = () => {
    const token = localStorage.getItem('operations_token');
    if (token) {
        sessionStorage.setItem('pressing_admin_token', token);
        // Persist the token as a kiosk token so the device stays enrolled across
        // tab close, reboots, and PWA restarts. The sliding refresh keeps it
        // rolling for up to 30 days of inactivity.
        localStorage.setItem(KIOSK_TOKEN_KEY, token);
        localStorage.removeItem('operations_token');
    } else {
        // Kiosk boot path: no admin session, but a persistent kiosk token exists.
        // Promote it so portal pages (which read from sessionStorage) work.
        const kiosk = localStorage.getItem(KIOSK_TOKEN_KEY);
        if (kiosk) sessionStorage.setItem('pressing_admin_token', kiosk);
    }
};

export const clearPressingSession = () => {
    const token = sessionStorage.getItem('pressing_admin_token');
    if (token) localStorage.setItem('operations_token', token);
    sessionStorage.removeItem('pressing_admin_token');
    sessionStorage.removeItem('presser_session');
    // Reset portal-user language preference; the next PIN-login will re-set
    // it based on the new user's record. Keeps the PIN screen / next user
    // from inheriting a previous user's Spanish setting.
    sessionStorage.removeItem('opshub_portal_language');
};

export const getPresserSession = () => {
    try {
        return JSON.parse(sessionStorage.getItem('presser_session') || 'null');
    } catch { return null; }
};

export const setPresserSession = (data) => {
    sessionStorage.setItem('presser_session', JSON.stringify(data));
};

export const pressingFetch = async (url, options = {}) => {
    // Use the stashed admin token for all pressing portal API calls.
    // Fall back to the persistent kiosk token if the session was wiped
    // (e.g. iPad rebooted and the tab is re-loading into the portal).
    let token = sessionStorage.getItem('pressing_admin_token')
             || localStorage.getItem(KIOSK_TOKEN_KEY);
    if (token && !sessionStorage.getItem('pressing_admin_token')) {
        sessionStorage.setItem('pressing_admin_token', token);
    }
    if (!token) {
        clearPressingSession();
        window.location.href = '/login';
        throw new Error('No pressing admin token');
    }
    const headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        ...portalOperatorHeaders(),
    };
    const res = await fetch(url, { ...options, headers });
    applyRefreshedToken(res);
    if (res.status === 401) {
        // Admin token expired — clear everything including kiosk and force re-login
        clearPressingSession();
        localStorage.removeItem('operations_token');
        clearKioskToken();
        window.location.href = '/login';
        throw new Error('Authentication required');
    }
    return res;
};
